<template>
    <div>
        <div class="flex items-center justify-between mb-4">
            <div class="text-slate-700 font-extrabold">
                API Destination
            </div>
            <experimental-pill type="beta"></experimental-pill>
        </div>

        <div v-if="loading || apiDestinations.length"
            class="flex flex-col gap-2 lg:flex-row">
            <vx-selectable placeholder="Select an API Destination"
                v-model="apiDestination"
                :options="apiDestinations"
                :loading="loading"
                label="friendly_name"
                :clearable="false"
                :error="apiDestinationError"
                size="lg"></vx-selectable>
            <vx-popover v-if="apiDestination?.id"
                class="w-full lg:w-auto"
                hover
                :arrow="true"
                placement="top">
                <vx-button block
                    :disabled="!isAdminOrOwner"
                    @click.prevent="showModalForm = true;"
                    type="button"
                    color="muted"
                    size="lg"
                    outline>Edit <font-awesome-icon widthAuto :icon="faPen"></font-awesome-icon></vx-button>
                <template v-if="!isAdminOrOwner"
                    v-slot:content>
                    You do not have permission to edit.
                </template>
            </vx-popover>
            <vx-popover hover
                class="w-full lg:w-auto"
                :arrow="true"
                placement="top">
                <vx-button block
                    :disabled="!isAdminOrOwner"
                    @click.prevent="addNewApiDestination()"
                    type="button"
                    color="muted"
                    size="lg">Add New <font-awesome-icon widthAuto :icon="faPlus"></font-awesome-icon></vx-button>
                <template v-if="!isAdminOrOwner"
                    v-slot:content>
                    You do not have permission to create.
                </template>
            </vx-popover>
        </div>
        <div v-else>
            <p v-if="!isAdminOrOwner"
                class="text-sm mb-4 text-slate-700">You do not have permission to create an API Destination.
                Contact an admin for your account to update your permissions or create the API Destination for you.</p>
            <vx-button :disabled="!isAdminOrOwner"
                @click.prevent="addNewApiDestination()"
                type="button"
                color="muted"
                size="lg"
                block>Add New <font-awesome-icon widthAuto :icon="faPlus"></font-awesome-icon></vx-button>
        </div>

        <div v-if="apiDestination"
            class="mt-6 flex flex-col gap-y-4">
            <div>
                <div class="text-slate-700 font-extrabold">
                    URL Variables
                </div>
                <div class="text-slate-500 text-sm">
                    If you need help adding URL variables <a class="underline"
                        href="https://support.voxie.com"
                        target="_blank"
                        rel="noopener noreferrer">check out this documentation</a>.
                </div>
            </div>
            <div>
                <div class="font-bold text-base">Endpoint</div>
                <div class="font-mono break-words">{{ apiDestination.uri_template }}</div>
            </div>
            <div v-for="(uriBinding, index) in uriBindings"
                :key="uriBinding.key">
                <div class="font-mono">{{ uriBinding.key }}</div>
                <vx-input v-model="uriBindings[index].value"
                    @blur="emitData()"
                    size="lg">
                    <template v-slot:box>
                        <font-awesome-icon widthAuto class="text-slate-800/30"
                            :icon="faCodeSimple"></font-awesome-icon>
                    </template>
                </vx-input>
            </div>
        </div>
        <div v-if="apiDestination"
            class="bg-slate-50 rounded-2xl mt-6 p-4 lg:px-6">
            <div class="flex items-center justify-between mb-0">
                <div class="text-slate-700 font-extrabold flex items-center gap-2">
                    <font-awesome-icon widthAuto :icon="faInboxOut"
                        class="text-base"></font-awesome-icon>
                    Payload
                </div>
                <div class="flex items-center gap-2">
                    <experimental-pill type="beta"></experimental-pill>
                    <vx-button size="sm"
                        color="muted"
                        @click="payloadModal = true">
                        <font-awesome-icon widthAuto :icon="faCircleQuestion"></font-awesome-icon>
                    </vx-button>
                </div>
            </div>
            <div class="text-slate-500 mb-4">
                We use <a class="underline"
                    href="https://jmespath.org/"
                    target="_blank"
                    rel="noopener noreferrer">JMESPath</a> to query and transform data. Modify the payload to be sent to the
                API below.
            </div>
            <div>
                <vx-code-editor v-model="payloadTemplate"
                    :placeholder="placeholder"></vx-code-editor>
            </div>
        </div>

        <api-destination-modal-form v-model:visible="showModalForm"
            :apiDestinationId="apiDestination?.id"
            @upserted="upsertAndSelect($event)"></api-destination-modal-form>

        <vx-modal v-model:visible="payloadModal"
            :padding="false"
            size="lg">
            <template v-slot:header>
                <div class="pt-6 px-6 flex items-center gap-2">
                    Sample Event Data
                </div>
            </template>
            <template v-slot:subheader>
                <div class="px-6">The following data is available for you to use in the payload.</div>
            </template>

            <div class="p-6">
                <vx-code-editor :modelValue="payload"
                    disabled></vx-code-editor>
            </div>
        </vx-modal>
    </div>
</template>

<script setup>
import { VxButton, VxInput, VxCodeEditor, VxSelectable, VxPopover, VxModal } from '@voxie/frontend-components';
import { computed, onMounted, ref, watch } from 'vue';
import ExperimentalPill from '~/components/general/ExperimentalPill.vue';
import { apiDestinationsClient } from '~/services';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCircleQuestion, faCodeSimple, faInboxOut, faPen, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { useToasted } from '../../../../composables/useToasted';
import { uniqBy } from '~/utils/helpers';
import ApiDestinationModalForm from '~/components/settings/integrations/api-destinations/form/ApiDestinationModalForm.vue';

const toasted = useToasted();

const props = defineProps({
    modelValue: {
        type: Object,
        required: true,
    },
    event: String,
});

const emit = defineEmits(['update:modelValue']);

const isAdminOrOwner = Spark.isAdminOrOwner;

const showModalForm = ref(false);

const placeholder = `{
  "exampleKey.$": "contact.first_name"
}`;

const loading = ref(false);
const apiDestinations = ref([]);

const teamId = Spark.state.currentTeam.id;

const apiDestination = ref(null)
const apiDestinationError = ref(null)
const uriBindings = ref([])
const payloadTemplate = ref('')

const mounted = ref(false);

const emitData = () => {
    const data = {
        team_id: teamId,
        api_destination_id: apiDestination.value?.id,
        uri_bindings: uriBindings.value.reduce((bindings, binding) => ({
            ...bindings,
            [binding.key]: binding.value,
        }), {}),
        payload_template: payloadTemplate.value
    };
    emit('update:modelValue', data);
};

const upsertAndSelect = (event) => {
    toasted.success('API Destination saved and selected')
    apiDestinations.value.unshift(event);
    apiDestinations.value = uniqBy(apiDestinations.value, 'id');
    apiDestination.value = event;
}

const addNewApiDestination = () => {
    showModalForm.value = true
    apiDestination.value = null;
}

watch(apiDestination, () => {
    apiDestinationError.value = null;
});

watch([apiDestination, uriBindings, payloadTemplate], () => {
    if (mounted.value) {
        emitData();
    }
})

const computeUriBindings = () => {
    uriBindings.value = apiDestination.value?.uri_variables?.reduce((bindings, variable) => [...bindings, {
        key: variable,
        value: props.modelValue?.uri_bindings?.[variable] || ''
    }], []) || [];
}

watch(apiDestination, () => computeUriBindings())

onMounted(async () => {
    if (props.modelValue?.api_destination_id) {
        try {
            apiDestination.value = (await apiDestinationsClient.get(teamId, props.modelValue?.api_destination_id)).data;
            apiDestinations.value = [apiDestination.value]
        } catch (e) {
            console.error(e);
            apiDestinationError.value = 'The previously connect API Destination record is missing. Select a new one.';
        }
    }

    payloadTemplate.value = props.modelValue?.payload_template || '';

    try {
        loading.value = true;
        apiDestinations.value = apiDestinations.value.concat((await apiDestinationsClient.list(teamId, {
            per_page: 50,
            sort_dir: 'desc',
        })).data.data)
        apiDestinations.value = uniqBy(apiDestinations.value, 'id');

        mounted.value = true;
    } catch (e) {
        console.error(e)
        toasted.global.platform_error();
    } finally {
        loading.value = false;
    }
});
const samplePayload = {
    "team": {
        "id": 1,
        "name": "Acme",
        "custom_attributes": {
            "store_name": "Steve's Store"
        }
    },
    "contact": {
        "id": 2,
        "phone": "+15555551234",
        "email": "dave@example.com",
        "first_name": "Dave",
        "last_name": "Campbell",
        "time_zone": "America/New_York",
        "entry_channel": "website",
        "membership_status": "premium",
        "lifecycle_status": "converted",
        "entry_point": "legacy",
        "tags": ["foo", "bar"],
        "custom_attributes": {
            "fav_car": "Lambo"
        },
        "subscriptions": {
            "transactional": "unknown",
            "marketing": "opt_out"
        },
        "external_ids": {
            "hubspot_id": "12345",
        },
    },
    "campaign": {
        "id": 3,
        "name": "Quick Blast 3",
        "status": "scheduled",
        "audience_type": "marketing",
        "duration_type": "one_time"
    },
    "group": {
        "external_id": "acme",
        "group_name": "Acme",
        "org_unit_name": "Seattle Office",
        "custom_attributes": {
            "store_location": "Downtown"
        }
    },
    "tag": {
        "name": "likes_lambos"
    },
    "subscription": {
        "type": "marketing",
        "status": "opt_in"
    },
    "dynamic_field": {
        "type": "company",
        "choices": "null",
        "custom_attribute_key": "extracted_field"
    },
    "custom_attribute": {
        "key": "fav_car",
        "value": "Lambo"
    },
    "entry_channel": {
        "old_value": "website",
        "new_value": "mobile_app"
    },
    "membership_status": {
        "old_value": "free",
        "new_value": "premium"
    },
    "lifecycle_status": {
        "old_value": "lead",
        "new_value": "converted"
    },
    "message": {
        "id": 4,
        "to": "+15555551234",
        "from": "42420",
        "type": "sms",
        "content": "Hi there, buy my product!",
        "direction": "outbound",
        "status": "delivered",
        "origin_type": "campaign_message",
        "origin_id": 5
    },
    "short_url": {
        "short_code": "qwerty",
        "long_url": "https://example.com/"
    },
    "antecedent": {
        "id": 4,
        "to": "+15555551234",
        "from": "42420",
        "type": "sms",
        "content": "Hi there, buy my product!",
        "direction": "outbound",
        "status": "delivered",
        "origin_type": "campaign_message",
        "origin_id": 5
    },
    "preferred_group": {
        "external_id": "acme",
        "group_name": "Acme",
        "org_unit_name": "Seattle Office",
        "custom_attributes": {
            "account_type": "enterprise"
        }
    },
    "sender": {
        "type": "corporate",
        "id": null,
        "name": "Acme"
    }
};

const payloadModal = ref(false);
const payload = computed(() => {
    const data = {
        team: samplePayload.team,
        contact: samplePayload.contact,
        preferred_group: samplePayload.preferred_group,
    };

    if (['contact.campaign.completed', 'campaign.contact.filtered'].includes(props.event)) {
        data.campaign = samplePayload.campaign;
    }

    if (['contact.group.added', 'contact.group.removed'].includes(props.event)) {
        data.group = samplePayload.group;
    }

    if ([
        'contact.marketing_subscription_opt_in',
        'contact.marketing_subscription_opt_out',
        'contact.transactional_subscription_opt_in',
        'contact.transactional_subscription_opt_out'
    ].includes(props.event)) {
        data.subscription = samplePayload.subscription;
    }

    if (['contact.tag.added', 'contact.tag.removed'].includes(props.event)) {
        data.tag = samplePayload.tag;
    }

    if (['contact.custom_attribute.created', 'contact.custom_attribute.created_or_updated', 'contact.custom_attribute.updated'].includes(props.event)) {
        data.custom_attribute = samplePayload.custom_attribute;
    }

    if (['contact.entry_channel.updated'].includes(props.event)) {
        data.entry_channel = samplePayload.entry_channel;
    }

    if (['contact.membership_status.updated'].includes(props.event)) {
        data.membership_status = samplePayload.membership_status;
    }

    if (['contact.lifecycle_status.updated'].includes(props.event)) {
        data.lifecycle_status = samplePayload.lifecycle_status;
    }

    if (['message.created', 'message.inbound', 'message.outbound', 'message.short_url.clicked', 'message.dyn_field.extract_success', 'message.dyn_field.extract_failure'].includes(props.event)) {
        data.message = samplePayload.message;
        data.sender = samplePayload.sender;
    }

    if (['message.dyn_field.extract_success', 'message.dyn_field.extract_failure'].includes(props.event)) {
        data.dynamic_field = samplePayload.dynamic_field;
    }

    if (['message.short_url.clicked'].includes(props.event)) {
        data.short_url = samplePayload.short_url;
    }

    if (['message.dyn_field.extract_success', 'message.dyn_field.extract_failure', 'message.created', 'message.inbound'].includes(props.event)) {
        data.antecedent = samplePayload.antecedent;
    }

    return JSON.stringify(data, null, 4);
});
</script>
